<p-dialog [(visible)]="visible" [modal]="true" [style]="{ width: '50vw' }"
    [header]="readOnly() ? 'View Maintenance Record' : (isEditMode() ? 'Edit Maintenance Record' : 'New Maintenance Record')"
    [draggable]="false" [resizable]="false" class="p-fluid" (onHide)="onCancel()">

    <ng-template pTemplate="content">
        <div class="flex flex-col gap-4 py-2">

            <!-- Parent Asset (required when creating from global maintenance records page) -->
            <div class="field" *ngIf="showParentAssetPicker() || (requireParentAsset() && isEditMode())">
                <label for="parentAsset" class="font-medium mb-1 block">
                    Parent Asset
                    <span *ngIf="showParentAssetPicker()" class="text-red-500">*</span>
                </label>
                <p-autocomplete
                    *ngIf="showParentAssetPicker(); else parentAssetReadonly"
                    inputId="parentAsset"
                    [ngModel]="parentAsset()"
                    (ngModelChange)="onParentAssetModelChange($event)"
                    [suggestions]="filteredParentAssets()"
                    (completeMethod)="searchParentAssets($event)"
                    (onSelect)="onParentAssetSelect($event)"
                    (onClear)="onParentAssetClear()"
                    [dropdown]="true"
                    dropdownMode="current"
                    [forceSelection]="true"
                    [minLength]="0"
                    field="asset_code"
                    placeholder="Search asset code..."
                    [showClear]="true"
                    styleClass="w-full text-sm">
                    <ng-template let-asset pTemplate="item">
                        <div class="flex flex-col">
                            <span class="font-medium">{{ asset.asset_code }}</span>
                            <span *ngIf="asset.grp_no" class="text-xs text-gray-500">GRP: {{ asset.grp_no }}</span>
                        </div>
                    </ng-template>
                </p-autocomplete>
                <ng-template #parentAssetReadonly>
                    <input pInputText class="w-full" [value]="parentAsset()?.asset_code || record()?.asset_code || ''" disabled />
                </ng-template>
                <small class="text-red-500 block mt-1" *ngIf="parentAssetError()">{{ parentAssetError() }}</small>
            </div>

            <!-- Details -->
            <div class="field">
                <label for="maintenanceDetails" class="font-medium mb-1 block">Maintenance Details</label>
                <app-lcca-autocomplete id="maintenanceDetails" [(value)]="maintenanceDetails"
                    (lccaSelected)="onLccaSelected($event)" [disabled]="readOnly()"
                    placeholder="Search maintenance details..."></app-lcca-autocomplete>
            </div>

            <!-- Documentation File Upload -->
            <div class="field">
                <label class="font-medium mb-1 block">Maintenance Documentation</label>
                <div class="flex flex-col gap-2">
                    <!-- File display when file exists -->
                    <div *ngIf="displayFileName()" class="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-md">
                        <i class="pi pi-file text-blue-500 text-xl"></i>
                        <div class="flex-1 min-w-0">
                            <span class="text-sm font-medium truncate block">{{ displayFileName() }}</span>
                            <a *ngIf="maintenanceDocumentation() && !documentationFile()"
                               [href]="getFileUrl(maintenanceDocumentation())"
                               target="_blank"
                               class="text-xs text-blue-600 hover:underline">
                                View/Download
                            </a>
                        </div>
                        <p-button *ngIf="!readOnly()"
                            icon="pi pi-times"
                            (onClick)="removeFile()"
                            severity="danger"
                            [text]="true"
                            [rounded]="true"
                            size="small"
                            pTooltip="Remove file">
                        </p-button>
                    </div>

                    <!-- File input when no file or to replace -->
                    <div *ngIf="!readOnly()" class="flex items-center gap-2">
                        <input
                            type="file"
                            id="documentation-file-input"
                            (change)="onFileSelected($event)"
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                            class="hidden" />
                        <label
                            for="documentation-file-input"
                            class="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-md text-sm font-medium text-gray-700 transition-colors">
                            <i class="pi pi-upload"></i>
                            {{ displayFileName() ? 'Replace File' : 'Choose File' }}
                        </label>
                        <span *ngIf="!displayFileName()" class="text-sm text-gray-500">No file selected</span>
                    </div>

                    <!-- Read-only message when no file -->
                    <div *ngIf="readOnly() && !displayFileName()" class="text-sm text-gray-500 italic">
                        No documentation uploaded
                    </div>
                </div>
            </div>

            <!-- Dates Row -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="field">
                    <label for="maintenanceDate" class="font-medium mb-1 block">Maintenance Date</label>
                    <input type="date" id="maintenanceDate" pInputText [ngModel]="maintenanceDate()"
                        (ngModelChange)="maintenanceDate.set($event)" [disabled]="readOnly()" placeholder="Select Date"
                        class="w-full">
                </div>

                <div class="field">
                    <label for="nextMaintenanceDate" class="font-medium mb-1 block">Next Maintenance Date</label>
                    <input type="date" id="nextMaintenanceDate" pInputText [ngModel]="nextMaintenanceDate()"
                        (ngModelChange)="nextMaintenanceDate.set($event)" [disabled]="readOnly()"
                        placeholder="Select Date" class="w-full">
                </div>
            </div>

            <!-- Maintenance Type -->
            <div class="field">
                <label for="maintenanceType" class="font-medium mb-1 block">Maintenance Type <span *ngIf="!readOnly()"
                        class="text-red-500">*</span></label>
                <app-lcca-type-autocomplete id="maintenanceType" [value]="maintenanceType()"
                    (valueChange)="maintenanceType.set($event)"></app-lcca-type-autocomplete>
                <small class="text-red-500 block mt-1" *ngIf="maintenanceTypeError()">{{ maintenanceTypeError()
                    }}</small>
            </div>

            <!-- Financials Row -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div class="field">
                    <label for="quantity" class="font-medium mb-1 block">Quantity</label>
                    <p-inputGroup>
                        <p-inputGroupAddon>
                            <i class="pi pi-sort-numeric-up"></i>
                        </p-inputGroupAddon>
                        <p-inputNumber id="quantity" [ngModel]="quantity()" (ngModelChange)="quantity.set($event)"
                            mode="decimal" [minFractionDigits]="0" [maxFractionDigits]="2" [disabled]="readOnly()"
                            placeholder="0.00" class="w-full"></p-inputNumber>
                    </p-inputGroup>
                </div>

                <div class="field">
                    <label for="rate" class="font-medium mb-1 block">Rate</label>
                    <p-inputGroup>
                        <!-- <p-inputGroupAddon>
                            <span class="font-semibold text-xs">RM</span>
                        </p-inputGroupAddon> -->
                        <p-inputGroupAddon>
                            <i class="pi pi-dollar"></i>
                        </p-inputGroupAddon>
                        <p-inputNumber id="rate" [ngModel]="rate()" (ngModelChange)="rate.set($event)" mode="currency"
                            currency="MYR" locale="en-MY" [disabled]="readOnly()" placeholder="0.00"
                            class="w-full"></p-inputNumber>
                    </p-inputGroup>
                </div>

                <div class="field">
                    <label for="totalCost" class="font-medium mb-1 block">Total Cost</label>
                    <p-inputGroup>
                        <p-inputGroupAddon>
                            <i class="pi pi-dollar"></i>
                        </p-inputGroupAddon>
                        <p-inputNumber id="totalCost" [ngModel]="totalCost()" (ngModelChange)="totalCost.set($event)"
                            mode="currency" currency="MYR" locale="en-MY" [disabled]="readOnly()" placeholder="0.00"
                            class="w-full"></p-inputNumber>
                    </p-inputGroup>
                </div>
            </div>
        </div>
    </ng-template>

    <ng-template pTemplate="footer">
        <div class="flex justify-end gap-2">
            <p-button label="Cancel" icon="pi pi-times" (onClick)="onCancel()" severity="secondary"
                [text]="true"></p-button>
            <p-button *ngIf="!readOnly()" label="Save" icon="pi pi-check" (onClick)="onSave()" [loading]="loading()"
                severity="primary"></p-button>
        </div>
    </ng-template>

</p-dialog>